import React, { useEffect, useState, useRef } from "react";
import { useGetList, useNotify } from "react-admin";
import { useTokenOps } from "../../../components/TokenContext";
import { hostname } from "../../../providers/api";
import {
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from "@material-ui/core";
import LoadingButton from "../../project/components/LoadingButton";
import {
  GtinUploadSummary,
  gtinUploadNotice,
  readGtinUploadResponse,
} from "../gtinUploadResult";
import "./RawDataUploadSection.css";
import "./EcobalyseScoreSection.css";

const CalculationTab = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [gtinReferences, setGtinReferences] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadedName, setUploadedName] = useState("");
  const [runLoading, setRunLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [isTestEnvironment, setIsTestEnvironment] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [version, setVersion] = useState("Ecobalyse 2025");
  const fileRef = useRef(null);
  const notify = useNotify();
  const tokenOps = useTokenOps();
  const accountId = tokenOps.account?.data?.accountId;
  const {
    data: projectsById = {},
    ids: projectIds = [],
    loading: projectsLoading,
    error: projectsError,
  } = useGetList(
    "projects",
    { page: 1, perPage: 1000 },
    { field: "name", order: "ASC" },
    { accountId },
  );
  const projects = projectIds
    .map((id) => projectsById[id])
    .filter(Boolean);
  const selectedProjectIsAvailable = projects.some(
    (project) => project.id === selectedProjectId,
  );
  const calculationInProgress = runLoading || refreshLoading;
  const calculationActionsDisabled =
    !selectedProjectIsAvailable ||
    projectsLoading ||
    Boolean(projectsError) ||
    projects.length === 0 ||
    calculationInProgress;
  const projectHelperText = projectsLoading
    ? "Loading projects..."
    : projectsError
      ? "Unable to load projects."
      : projects.length === 0
        ? "No projects available for this account."
        : "Select the project whose GTIN references will be processed.";

  useEffect(() => {
    setSelectedProjectId("");
  }, [accountId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        notify('Please select a valid Excel file (.xlsx or .xls)', 'warning');
        e.target.value = null;
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      notify('Please select a file first', 'warning');
      return;
    }

    setUploadLoading(true);
    setUploadResult(null);
    setUploadedName(selectedFile.name);

    const formData = new FormData();
    formData.append('file', selectedFile);

    fetch(`${hostname}/ecobalyse/upload-gtin-excel`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + tokenOps.account?.accessToken,
      },
      body: formData,
    })
      .then(readGtinUploadResponse)
      .then((result) => {
        // Partial and failed uploads are reported by status, not only by HTTP code.
        setUploadResult(result);
        const notice = gtinUploadNotice(result);
        notify(notice.message, notice.type);
        if (result.status !== 'FAILED') {
          setSelectedFile(null);
          if (fileRef.current) {
            fileRef.current.value = null;
          }
        }
      })
      .catch((error) => {
        console.error('[GTIN Upload] Error:', error);
        notify(`Error: ${error.message}`, 'error');
      })
      .finally(() => {
        setUploadLoading(false);
      });
  };

  const handleRunScores = () => {
    if (!selectedProjectIsAvailable) {
      notify("Please select a project first", "warning");
      return;
    }

    setRunLoading(true);

    fetch(`${hostname}/ecobalyse/run-ecobalyse`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + tokenOps.account?.accessToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        projectId: selectedProjectId,
        test: isTestEnvironment,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorData) => {
            throw new Error(errorData.message || 'Error running scores');
          });
        }
        return response.json();
      })
      .then((data) => {
        console.log(data);

        const successCount = data.successes?.length || 0;
        const failureCount = data.failures?.length || 0;

        if (failureCount > 0 && successCount === 0) {
          // Solo fallos
          const firstFailure = data.failures[0];
          const errorMsg = firstFailure.errors?.join(', ') || 'Unknown error';
          notify(`Failed to calculate scores: ${errorMsg}`, 'error');
        } else if (successCount > 0 && failureCount === 0) {
          // Solo éxitos
          notify(`Scores calculated successfully for ${successCount} GTIN${successCount !== 1 ? 's' : ''}`, 'success');
        } else if (successCount > 0 && failureCount > 0) {
          // Mezcla de éxitos y fallos
          notify(`Scores calculated: ${successCount} succeeded, ${failureCount} failed`, 'warning');
        } else {
          // Sin resultados
          notify('No GTINs were processed', 'info');
        }
      })
      .catch((error) => {
        console.error('[Run Scores] Error:', error);
        notify(`Error: ${error.message}`, 'error');
      })
      .finally(() => {
        setRunLoading(false);
      });
  };

  const handleRefreshScores = () => {
    if (!selectedProjectIsAvailable) {
      notify("Please select a project first", "warning");
      return;
    }

    setRefreshLoading(true);

    fetch(`${hostname}/ecobalyse/refresh-ecobalyse`, {
      method: 'POST',
      headers: {
        'authorization': 'Bearer ' + tokenOps.account?.accessToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        projectId: selectedProjectId,
        test: isTestEnvironment,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorData) => {
            throw new Error(errorData.message || 'Error refreshing scores');
          });
        }
        return response.json();
      })
      .then((data) => {
        console.log(data);

        const successCount = data.successes?.length || 0;
        const failureCount = data.failures?.length || 0;

        if (failureCount > 0 && successCount === 0) {
          // Solo fallos
          const firstFailure = data.failures[0];
          const errorMsg = firstFailure.errors?.join(', ') || 'Unknown error';
          notify(`Failed to refresh scores: ${errorMsg}`, 'error');
        } else if (successCount > 0 && failureCount === 0) {
          // Solo éxitos
          notify(`Scores refreshed successfully for ${successCount} GTIN${successCount !== 1 ? 's' : ''}`, 'success');
        } else if (successCount > 0 && failureCount > 0) {
          // Mezcla de éxitos y fallos
          notify(`Scores refreshed: ${successCount} succeeded, ${failureCount} failed`, 'warning');
        } else {
          // Sin resultados
          notify('No GTINs were processed', 'info');
        }
      })
      .catch((error) => {
        console.error('[Refresh Scores] Error:', error);
        notify(`Error: ${error.message}`, 'error');
      })
      .finally(() => {
        setRefreshLoading(false);
      });
  };

  return (
    <div style={{ padding: "20px" }}>
      {/* Raw data upload section */}
      <div className="ecobalyse-raw-data-section">
        <h3 className="ecobalyse-raw-data-section__title">Raw data upload</h3>
        <div className="ecobalyse-raw-data-section__upload-area">
          <input
            type="file"
            ref={fileRef}
            onChange={handleFileChange}
            accept=".xlsx,.xls"
            className="ecobalyse-raw-data-section__file-input"
          />
          {selectedFile && (
            <div className="ecobalyse-raw-data-section__file-preview">
              <img src="/preview-xlsx.png" alt="Excel file" />
              <div className="ecobalyse-raw-data-section__file-name">
                {selectedFile.name}
              </div>
            </div>
          )}
        </div>

        <div className="ecobalyse-raw-data-section__controls">
          <div className="ecobalyse-raw-data-section__checkboxes">
            <label className="ecobalyse-raw-data-section__checkbox-label">
              <input
                type="checkbox"
                checked={gtinReferences}
                onChange={(e) => setGtinReferences(e.target.checked)}
              />
              GTIN References
            </label>
          </div>
          <LoadingButton
            color="primary"
            variant="contained"
            className="ecobalyse-raw-data-section__upload-button"
            onClick={handleUpload}
            loading={uploadLoading}
            disabled={!selectedFile}
          >
            Upload
          </LoadingButton>
        </div>
        <GtinUploadSummary result={uploadResult} uploadedName={uploadedName} />
      </div>

      {/* Ecobalyse Score section */}
      <div className="ecobalyse-score-section__divider"></div>

      <div className="ecobalyse-score-section">
        <h3 className="ecobalyse-score-section__title">Ecobalyse Score</h3>
        <div className="ecobalyse-score-section__content">
          <div style={{ marginBottom: "20px" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isTestEnvironment}
                  onChange={(e) => setIsTestEnvironment(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <span style={{ fontSize: "14px", fontWeight: 500 }}>
                  Environment: <strong>{isTestEnvironment ? 'Test' : 'Official'}</strong>
                </span>
              }
            />
          </div>
          <TextField
            select
            id="ecobalyse-project"
            label="Project"
            value={selectedProjectId}
            onChange={(event) => setSelectedProjectId(event.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
            disabled={
              projectsLoading ||
              Boolean(projectsError) ||
              projects.length === 0 ||
              calculationInProgress
            }
            helperText={projectHelperText}
            className="ecobalyse-score-section__project-selector"
          >
            <MenuItem value="" disabled>
              Select a project
            </MenuItem>
            {projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>
          <div className="ecobalyse-score-section__buttons-wrapper">
            <div className="ecobalyse-score-section__button-container">
              <LoadingButton
                color="primary"
                variant="contained"
                onClick={handleRunScores}
                loading={runLoading}
                disabled={calculationActionsDisabled}
                style={{
                  width: "400px",
                  maxWidth: "100%"
                }}
              >
                Run Scores
              </LoadingButton>
              <div className="ecobalyse-score-section__description">
                Get the Ecobalyse scores for pending or newly added GTINs in
                the selected project.
              </div>
            </div>
            <div className="ecobalyse-score-section__button-container">
              <LoadingButton
                color="secondary"
                variant="contained"
                onClick={handleRefreshScores}
                loading={refreshLoading}
                disabled={calculationActionsDisabled}
                style={{
                  width: "400px",
                  maxWidth: "100%"
                }}
              >
                Refresh Scores
              </LoadingButton>
              <div className="ecobalyse-score-section__description">
                Recalculate scores for all GTIN references in the selected
                project to ensure they&apos;re up to date.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculationTab;
